import { Chip, Stack, Typography } from '@mui/material';
import type { EmployerFootprintAdminViewDto, EmployerVerificationDetail } from '../../api/employerVerificationDashboardApi';
import { employerVerificationFinalChipColor } from '../../lib/employerVerificationFinalChip';
import { finalOutcomeLabelStored } from '../../lib/employerVerificationCopy';
import type { EmployerReviewSummaryViewModel } from '../../lib/employerReview/reviewReason.types';
import { EmployerInvestigationBody } from '../verification/EmployerInvestigationBody';
import { useEmployerEvidenceTourOptional } from './guidedTour/EmployerEvidenceTourContext';
import { FindingEvidenceSummary } from './FindingEvidenceSummary';
import { VERIFICATION_BRIEF_WORKFLOW_SUBTITLE } from './findingBriefSectionStyles';
import { GateAnalysisSection, VerificationEvidence } from './VerificationEvidence';

export function EmployerVerificationSections({
  reviewSummary,
  detail,
  footprint,
  dossier = null,
  pairDelta,
  detailLoading = false,
  section = 'both',
}: {
  reviewSummary: EmployerReviewSummaryViewModel;
  detail: EmployerVerificationDetail | null;
  footprint: EmployerFootprintAdminViewDto | null;
  dossier?: Parameters<typeof EmployerInvestigationBody>[0]['dossier'];
  pairDelta: {
    overallUnchanged: boolean;
    firstChangedGateTitle: string | null;
  } | null;
  detailLoading?: boolean;
  /** Split layout: brief early in the tab, gates after supporting evidence. */
  section?: 'brief' | 'gates' | 'both';
}) {
  const nav = useEmployerEvidenceTourOptional();
  const showBrief = section === 'both' || section === 'brief';
  const showGates = section === 'both' || section === 'gates';

  return (
    <Stack spacing={1.25}>
      {showBrief ? (
        <VerificationEvidence
          expanded={nav?.verificationOpen ?? false}
          onExpandedChange={(open) => nav?.setVerificationOpen(open)}
          subtitle={VERIFICATION_BRIEF_WORKFLOW_SUBTITLE}
        >
          <FindingEvidenceSummary
            findings={reviewSummary.evidenceFindings}
            conflict={reviewSummary.conflict}
            detail={detail}
            footprint={footprint}
            loading={detailLoading}
          />
        </VerificationEvidence>
      ) : null}

      {showGates ? (
        <VerificationEvidence
          id="employer-gate-analysis"
          title="Gate analysis"
          variant="gate-analysis"
          expanded={nav?.gateAnalysisOpen ?? false}
          onExpandedChange={(open) => nav?.setGateAnalysisOpen(open)}
          subtitle="Checkpoint tables — open Review on any row for full evidence"
        >
          <GateAnalysisSection
            title="Checkpoint results"
            subtitle="One table per gate"
            endAdornment={
              detail ? (
                <>
                  <Chip
                    size="small"
                    label={finalOutcomeLabelStored(detail.finalStatus)}
                    color={employerVerificationFinalChipColor(detail.finalStatus)}
                    variant="outlined"
                    sx={{ height: 22, fontWeight: 700 }}
                  />
                  {pairDelta ? (
                    <Chip
                      size="small"
                      label={pairDelta.overallUnchanged ? 'No changes vs prior' : 'Changes vs prior'}
                      variant="outlined"
                      sx={{
                        height: 22,
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        ...(pairDelta.overallUnchanged
                          ? {}
                          : {
                              borderColor: 'warning.main',
                              color: 'warning.dark',
                            }),
                      }}
                    />
                  ) : null}
                </>
              ) : null
            }
          >
            {!pairDelta?.overallUnchanged && pairDelta?.firstChangedGateTitle ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
                {pairDelta.firstChangedGateTitle}
              </Typography>
            ) : null}
            {detail ? (
              <EmployerInvestigationBody detail={detail} dossier={dossier} footprint={footprint} />
            ) : null}
          </GateAnalysisSection>
        </VerificationEvidence>
      ) : null}
    </Stack>
  );
}
